/**
 * Pure date and bucketing helpers behind the room availability search.
 *
 * Split out of reservations.ts on purpose: that file imports the Firebase SDK,
 * which cannot be loaded in a plain node test. These functions are the part
 * where a quiet mistake is expensive - if a booking lands in the wrong day
 * bucket, the search stops seeing it and confidently offers a room that is
 * already taken - so they live somewhere they can actually be tested.
 *
 * Nothing here touches Firestore. The only import is a type, which is erased
 * at runtime.
 */

import type { Timestamp } from 'firebase/firestore';

/** The slice of a reservation this file needs. */
export interface DayBucketable {
  startAt: Timestamp | Date;
  endAt: Timestamp | Date;
}

/** How far a search may reach, and the backstop on the bucketing loop. */
export const MAX_RESERVATION_RANGE_DAYS = 60;

/** YYYY-MM-DD in LOCAL time, so a calendar slot is naturally a "day". */
export function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * A reservation timestamp as a Date, or null if the field is missing or is not
 * a real Timestamp. Demo and legacy docs have both, and an unguarded
 * .toDate() on one of those took the Command Center down once already.
 */
export function reservationDate(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const ts = value as { toDate?: () => Date } | null | undefined;
  if (!ts || typeof ts.toDate !== 'function') return null;
  try {
    const d = ts.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

/**
 * Group bookings by every local day they touch, so a day search filters a
 * handful of rows instead of the whole range.
 *
 * findFreeSlotsForDay re-filters and re-sorts the entire list it is handed,
 * once per room. Called per day across a range that is itself the source of
 * the list, that is quadratic in days: measured at 253ms for 60 days across 5
 * rooms and 1.2s at 10 rooms, which is a frozen phone on every keystroke in
 * the duration box. Bucketing once collapses it to linear.
 *
 * A booking is filed under every day it overlaps, not only the one it starts
 * on, so an overnight block still blocks the morning it runs into.
 */
export function groupReservationsByDay<T extends DayBucketable>(
  reservations: T[]
): Map<string, T[]> {
  const byDay = new Map<string, T[]>();
  for (const r of reservations) {
    const start = reservationDate(r.startAt);
    const end = reservationDate(r.endAt);
    if (!start || !end) continue;
    const cursor = new Date(start);
    cursor.setHours(0, 0, 0, 0);
    // Bounded so a malformed booking with a runaway end date cannot spin here.
    for (let i = 0; i < MAX_RESERVATION_RANGE_DAYS + 2; i += 1) {
      if (cursor.getTime() > end.getTime()) break;
      const key = localDateKey(cursor);
      const list = byDay.get(key);
      if (list) list.push(r);
      else byDay.set(key, [r]);
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  return byDay;
}

/**
 * Every day from start to end inclusive, capped at MAX_RESERVATION_RANGE_DAYS.
 *
 * Calendar-day arithmetic on local midnight, so a range that crosses a
 * daylight-saving boundary does not gain or lose a day to a 23 or 25 hour
 * jump.
 */
export function daysBetween(start: Date, end: Date): Date[] {
  const out: Date[] = [];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  const last = new Date(end);
  last.setHours(0, 0, 0, 0);
  while (cursor.getTime() <= last.getTime() && out.length < MAX_RESERVATION_RANGE_DAYS) {
    out.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}
